import { ActionFunction, redirect, replace } from 'react-router-dom';
import { client } from './client';
import {
  CreateAccountDocument,
  CreateAccountMutationVariables,
  CreateBusinessPoliciesDocument,
  CreateBusinessPoliciesMutation,
  CreateBusinessPoliciesMutationVariables,
  UpdateAccountDocument,
  UpdateAccountMutation,
  UpdateAccountMutationVariables,
  UpdateBusinessPolicyMutationVariables,
  UpdateBusinessPolicyMutation,
  UpdateBusinessPolicyDocument,
  UploadPolicyFileDocument,
  UploadPolicyFileMutation,
  UploadPolicyFileMutationVariables,
  FileType,
  DeleteFileMutationVariables,
  DeleteFileMutation,
  DeleteFileDocument,
  AddPolicyExplorerConversationMessageDocument,
  AddPolicyExplorerConversationMessageMutation,
  AddPolicyExplorerConversationMessageMutationVariables,
  CreateAccountMutation,
  CreateCertificateMutationVariables,
  CreateCertificateMutation,
  CreateCertificateDocument,
  ResolveServiceRequestDocument,
  ResolveServiceRequestMutation,
  ResolveServiceRequestMutationVariables,
  UpdateServiceRequestMutation,
  UpdateServiceRequestDocument,
  UpdateServiceRequestMutationVariables,
  AddServiceRequestCommentMutation,
  AddServiceRequestCommentMutationVariables,
  AddServiceRequestCommentDocument,
  CreateCustomPresetMutation,
  CreateCustomPresetMutationVariables,
  CreateCustomPresetDocument,
  UpdateCustomPresetMutationVariables,
  UpdateCustomPresetMutation,
  UpdateCustomPresetDocument,
  UpdateAgencyUserPreferencesDocument,
  UpdateAgencyUserPreferencesMutation,
  UpdateAgencyUserPreferencesMutationVariables,
  CreateHtmlDocumentTemplateMutationVariables,
  CreateHtmlDocumentTemplateMutation,
  CreateHtmlDocumentTemplateDocument,
  UpdateHtmlDocumentTemplateMutationVariables,
  UpdateHtmlDocumentTemplateDocument,
  UpdateHtmlDocumentTemplateMutation,
  CreateKnowledgeBaseTextDocumentMutationVariables,
  CreateKnowledgeBaseTextDocumentMutation,
  CreateKnowledgeBaseTextDocumentDocument,
  UploadKnowledgeBaseFileDocumentMutationVariables,
  UploadKnowledgeBaseFileDocumentMutation,
  UploadKnowledgeBaseFileDocumentDocument,
  UpdateKnowledgeBaseDocumentMutationVariables,
  UpdateKnowledgeBaseDocumentDocument,
  UpdateKnowledgeBaseDocumentMutation,
  DeleteKnowledgeBaseDocumentDocument,
  DeleteKnowledgeBaseDocumentMutation,
  DeleteKnowledgeBaseDocumentMutationVariables,
  UpdateCaraWorkspacesMutationVariables,
  UpdateCaraWorkspacesDocument,
  UpdateCaraWorkspacesMutation,
  DeleteBusinessPolicyMutation,
  DeleteBusinessPolicyMutationVariables,
  DeleteBusinessPolicyDocument,
  ReinstateBusinessPolicyDocument,
  ReinstateBusinessPolicyMutation,
  ReinstateBusinessPolicyMutationVariables,
  RenewBusinessPolicyDocument,
  RenewBusinessPolicyMutation,
  RenewBusinessPolicyMutationVariables,
  CancelBusinessPolicyDocument,
  CancelBusinessPolicyMutation,
  CancelBusinessPolicyMutationVariables,
  CreateAgencyUserMutation,
  CreateAgencyUserMutationVariables,
  CreateAgencyUserDocument,
  CreateIntegrationDocument,
  CreateIntegrationMutation,
  CreateIntegrationMutationVariables,
  CaraWorkspacePreset
} from './graphql';
import { mutate } from '@oysterjs/corev2/graphql/actions';

export const createAccount: ActionFunction = async ({ request }) =>
  mutate<CreateAccountMutation, CreateAccountMutationVariables>(
    client,
    { mutation: CreateAccountDocument, variables: { input: await request.json() } },
    async (data) => redirect(`/app/accounts/${data?.createAccount?.id}/overview`)
  );

export const updateAccount: ActionFunction = async ({ params, request }) =>
  mutate<UpdateAccountMutation, UpdateAccountMutationVariables>(
    client,
    {
      mutation: UpdateAccountDocument,
      variables: { id: params.id || '', input: await request.json() }
    },
    async (data) => redirect(`/app/accounts/${data?.updateAccount?.id}/overview`)
  );

export const createBusinessPolicy: ActionFunction = async ({ request }) =>
  mutate<CreateBusinessPoliciesMutation, CreateBusinessPoliciesMutationVariables>(
    client,
    {
      mutation: CreateBusinessPoliciesDocument,
      variables: { input: await request.json() },
      errorPolicy: 'all'
    },
    async (data) => redirect(`/app/policies/${data?.createBusinessPolicies?.[0]?.id}/overview`)
  );

export const updateBusinessPolicy: ActionFunction = async ({ params, request }) =>
  mutate<UpdateBusinessPolicyMutation, UpdateBusinessPolicyMutationVariables>(
    client,
    {
      mutation: UpdateBusinessPolicyDocument,
      variables: { id: params.id || '', input: await request.json() }
    },
    async (data) => redirect(`/app/policies/${data?.updateBusinessPolicy?.id}/overview`)
  );

export const deleteBusinessPolicy: ActionFunction = async ({ params }) =>
  mutate<DeleteBusinessPolicyMutation, DeleteBusinessPolicyMutationVariables>(
    client,
    {
      mutation: DeleteBusinessPolicyDocument,
      variables: { id: params.id || '' }
    },
    async () => replace(`/app/policies`)
  );

export const cancelBusinessPolicy: ActionFunction = async ({ params, request }) =>
  mutate<CancelBusinessPolicyMutation, CancelBusinessPolicyMutationVariables>(
    client,
    {
      mutation: CancelBusinessPolicyDocument,
      variables: { id: params.id || '', input: await request.json() }
    },
    async (data) => replace(`/app/policies/${data?.cancelBusinessPolicy?.id}/overview`)
  );

export const renewBusinessPolicy: ActionFunction = async ({ params, request }) =>
  mutate<RenewBusinessPolicyMutation, RenewBusinessPolicyMutationVariables>(
    client,
    {
      mutation: RenewBusinessPolicyDocument,
      variables: { id: params.id || '', input: await request.json() }
    },
    async (data) => replace(`/app/accounts/${data?.renewBusinessPolicy?.account.id}/policies`)
  );

export const reinstateBusinessPolicy: ActionFunction = async ({ params, request }) =>
  mutate<ReinstateBusinessPolicyMutation, ReinstateBusinessPolicyMutationVariables>(
    client,
    {
      mutation: ReinstateBusinessPolicyDocument,
      variables: { id: params.id || '', input: await request.json() }
    },
    async (data) => redirect(`/app/policies/${data?.reinstateBusinessPolicy?.id}/overview`)
  );

export const uploadPolicyFile: ActionFunction = async ({ params, request }) => {
  const formData = await request.formData();
  return mutate<UploadPolicyFileMutation, UploadPolicyFileMutationVariables>(client, {
    mutation: UploadPolicyFileDocument,
    variables: {
      policyId: params.id || '',
      file: {
        notes: formData.get('notes') as string,
        type: formData.get('type') as FileType,
        visible: formData.get('visible') === 'true',
        file: formData.get('file') as File
      }
    }
  });
};

export const deleteFile: ActionFunction = async ({ request }) =>
  mutate<DeleteFileMutation, DeleteFileMutationVariables>(client, {
    mutation: DeleteFileDocument,
    variables: { fileId: (await request.json()).fileId }
  });

export const addPolicyExplorerConversationMessage: ActionFunction = async ({ params, request }) =>
  mutate<
    AddPolicyExplorerConversationMessageMutation,
    AddPolicyExplorerConversationMessageMutationVariables
  >(client, {
    mutation: AddPolicyExplorerConversationMessageDocument,
    variables: { policyId: params.id || '', message: (await request.json()).message }
  });

export const createCertificateOfLiabilityInsurance: ActionFunction = async ({ params, request }) =>
  mutate<CreateCertificateMutation, CreateCertificateMutationVariables>(
    client,
    {
      mutation: CreateCertificateDocument,
      variables: { input: await request.json() }
    },
    async (data) =>
      replace(`/app/accounts/${params.id}/certificates/${data?.createCertificate?.id}`)
  );

export const resolveServiceRequest: ActionFunction = async ({ params, request }) => {
  const formData = await request.formData();
  return mutate<ResolveServiceRequestMutation, ResolveServiceRequestMutationVariables>(client, {
    mutation: ResolveServiceRequestDocument,
    variables: {
      id: params.id || '',
      input: {
        notes: formData.get('notes') as string,
        files: formData.getAll('files') as File[]
      }
    }
  });
};

export const updateServiceRequest: ActionFunction = async ({ params, request }) => {
  return mutate<UpdateServiceRequestMutation, UpdateServiceRequestMutationVariables>(client, {
    mutation: UpdateServiceRequestDocument,
    variables: { id: params.id || '', input: await request.json() }
  });
};

export const addServiceRequestComment: ActionFunction = async ({ params, request }) => {
  return mutate<AddServiceRequestCommentMutation, AddServiceRequestCommentMutationVariables>(
    client,
    {
      mutation: AddServiceRequestCommentDocument,
      variables: { id: params.id || '', input: await request.json() }
    }
  );
};

export const createCustomPreset: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  return mutate<CreateCustomPresetMutation, CreateCustomPresetMutationVariables>(
    client,
    {
      mutation: CreateCustomPresetDocument,
      variables: {
        input: {
          allowFileUpload: formData.get('allowFileUpload') === 'true',
          basePrompt: formData.get('basePrompt') as string,
          description: formData.get('description') as string,
          fileIds: formData.getAll('fileIds') as string[] | null,
          inheritFromPreset: formData.get('inheritFromPreset') as CaraWorkspacePreset | null,
          title: formData.get('title') as string,
          uploadFiles: formData.getAll('uploadFiles') as File[]
        }
      }
    },
    async () => replace(`/app/presets`)
  );
};

export const updateCustomPreset: ActionFunction = async ({ params, request }) => {
  const formData = await request.formData();
  return mutate<UpdateCustomPresetMutation, UpdateCustomPresetMutationVariables>(
    client,
    {
      mutation: UpdateCustomPresetDocument,
      variables: {
        id: params.id || '',
        input: {
          allowFileUpload: formData.get('allowFileUpload') === 'true',
          basePrompt: formData.get('basePrompt') as string,
          description: formData.get('description') as string,
          fileIds: formData.getAll('fileIds') as string[] | null,
          inheritFromPreset: formData.get('inheritFromPreset') as CaraWorkspacePreset | null,
          title: formData.get('title') as string,
          uploadFiles: formData.getAll('uploadFiles') as File[]
        }
      }
    },
    async () => replace(`/app/presets`)
  );
};

export const updateAgencyUserPreferences: ActionFunction = async ({ request }) =>
  mutate<UpdateAgencyUserPreferencesMutation, UpdateAgencyUserPreferencesMutationVariables>(
    client,
    { mutation: UpdateAgencyUserPreferencesDocument, variables: { input: await request.json() } }
  );

export const createHtmlDocumentTemplate: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  return mutate<CreateHtmlDocumentTemplateMutation, CreateHtmlDocumentTemplateMutationVariables>(
    client,
    {
      mutation: CreateHtmlDocumentTemplateDocument,
      variables: {
        input: {
          title: formData.get('title') as string,
          description: formData.get('description') as string,
          baseStyleId: (formData.get('baseStyleId') as string | null) || null,
          prefixFileId: (formData.get('prefixFileId') as string | null) || null,
          suffixFileId: (formData.get('suffixFileId') as string | null) || null,
          uploadPrefixFile: (formData.get('uploadPrefixFile') as File | null) || null,
          uploadSuffixFile: (formData.get('uploadSuffixFile') as File | null) || null
        }
      }
    },
    async (data) => replace(`/app/styles/${data?.createHtmlDocumentTemplate?.id}`)
  );
};

export const updateHtmlDocumentTemplate: ActionFunction = async ({ params, request }) => {
  const formData = await request.formData();
  return mutate<UpdateHtmlDocumentTemplateMutation, UpdateHtmlDocumentTemplateMutationVariables>(
    client,
    {
      mutation: UpdateHtmlDocumentTemplateDocument,
      variables: {
        id: params.id || '',
        input: {
          title: formData.get('title') as string,
          description: formData.get('description') as string,
          baseStyleId: (formData.get('baseStyleId') as string | null) || null,
          prefixFileId: (formData.get('prefixFileId') as string | null) || null,
          suffixFileId: (formData.get('suffixFileId') as string | null) || null,
          uploadPrefixFile: (formData.get('uploadPrefixFile') as File | null) || null,
          uploadSuffixFile: (formData.get('uploadSuffixFile') as File | null) || null
        }
      }
    },
    async (data) => replace(`/app/styles/${data?.updateHtmlDocumentTemplate?.id}`)
  );
};

export const createKnowledgeBaseTextDocument: ActionFunction = async ({ params, request }) =>
  mutate<CreateKnowledgeBaseTextDocumentMutation, CreateKnowledgeBaseTextDocumentMutationVariables>(
    client,
    {
      mutation: CreateKnowledgeBaseTextDocumentDocument,
      variables: { knowledgeBaseId: params.id || '', input: await request.json() }
    },
    async (data) => replace(`/app/knowledge/${data?.createKnowledgeBaseTextDocument?.id}`)
  );

export const uploadKnowledgeBaseFileDocument: ActionFunction = async ({ params, request }) => {
  const formData = await request.formData();
  return mutate<
    UploadKnowledgeBaseFileDocumentMutation,
    UploadKnowledgeBaseFileDocumentMutationVariables
  >(client, {
    mutation: UploadKnowledgeBaseFileDocumentDocument,
    variables: {
      knowledgeBaseId: params.id || '',
      input: { files: formData.getAll('files') as File[] }
    }
  });
};

export const updateKnowledgeBaseDocument: ActionFunction = async ({ params, request }) =>
  mutate<UpdateKnowledgeBaseDocumentMutation, UpdateKnowledgeBaseDocumentMutationVariables>(
    client,
    {
      mutation: UpdateKnowledgeBaseDocumentDocument,
      variables: { id: params.id || '', input: await request.json() }
    },
    async (data) => replace(`/app/knowledge/${data?.updateKnowledgeBaseDocument?.id}`)
  );

export const deleteKnowledgeBaseDocument: ActionFunction = async ({ params }) =>
  mutate<DeleteKnowledgeBaseDocumentMutation, DeleteKnowledgeBaseDocumentMutationVariables>(
    client,
    { mutation: DeleteKnowledgeBaseDocumentDocument, variables: { id: params.id || '' } },
    async () => replace(`/app/knowledge`)
  );

export const archiveCaraWorkspaces: ActionFunction = async ({ request }) =>
  mutate<UpdateCaraWorkspacesMutation, UpdateCaraWorkspacesMutationVariables>(client, {
    mutation: UpdateCaraWorkspacesDocument,
    variables: { workspaceIds: (await request.json()).workspaceIds, input: { archive: true } }
  });

export const createAgencyUser: ActionFunction = async ({ request }) =>
  mutate<CreateAgencyUserMutation, CreateAgencyUserMutationVariables>(client, {
    mutation: CreateAgencyUserDocument,
    variables: { input: await request.json() }
  });

export const createIntegration: ActionFunction = async ({ request }) =>
  mutate<CreateIntegrationMutation, CreateIntegrationMutationVariables>(
    client,
    {
      mutation: CreateIntegrationDocument,
      variables: { input: await request.json() }
    },
    async (data) => redirect(`/app/settings/integrations/manage/${data?.createIntegration?.id}`)
  );
